import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { IconCloseSvg } from "@/components/svgs";
import { Divider, Row, Col, Modal } from "antd";
import { RAButtonPrimary, RATextInput } from "@/components/customs";
import { fetchUpdateUserInfo, selectAuthState } from "@/stores/slices/authSlice";
import { RASelectCountry, RASelectUSStates } from "@/components/atoms";
import { NOTIFICATION_TYPE, US_SHORT_CODE } from "@/config/constants";
import { toast } from "@/modules/toast";

const initFormUserProfile = {
	email: "",
	avatar: "",
	first_name: "",
	last_name: "",
	phone: "",
	company_name: "",
	address: {
		zip_code: "",
		address: "",
		address2: "",
		state: "",
		city: "",
		country: null,
	},
};

export const ModalEditMyProfile = ({ visible = false, onClose }) => {
	const dispatch = useDispatch();
	const { user } = useSelector(selectAuthState);

	const [loading, setLoading] = useState(false);
	const [userProfileForm, setUserProfileForm] = useState(initFormUserProfile);
	const [errors, setErrors] = useState({});

	const onSubmitEditUserProfile = async () => {
		if (loading) return;

		// set loading
		setLoading(true);

		let payload = userProfileForm;
		await dispatch(fetchUpdateUserInfo(payload));
		onClose?.();

		setTimeout(() => {
			setLoading(false);
		}, 300);
	};

	useEffect(() => {
		if (visible && user) {
			setUserProfileForm({
				email: user?.email,
				first_name: user?.first_name,
				last_name: user?.last_name,
				phone: user?.phone,
				company_name: user?.company_name,
				address: {
					zip_code: user?.address?.zip_code,
					address: user?.address?.address,
					address2: user?.address?.address2,
					state: user?.address?.state,
					city: user?.address?.city,
					country: user?.address?.country,
				},
			});
		}
	}, [visible, user]);

	return (
		<Modal
			centered
			open={visible}
			data-testid="EditProfile_modal"
			closeIcon={<IconCloseSvg />}
			onOk={() => {}}
			onCancel={() => onClose(false)}
			footer={null}
			width="720px"
			wrapClassName="auth-wrapper"
			style={{ padding: 0 }}
		>
			<FormContainer>
				<FormInputWrapper>
					<TitleText>Edit Profile</TitleText>
					<Divider />
					<Row gutter={[20, 12]}>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_firstNameInput"
								title="First Name"
								placeholder="Enter First Name"
								value={userProfileForm?.first_name}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										first_name: val,
									});
									if (errors && errors.first_name) {
										delete errors.first_name;
										setErrors(errors);
									}
								}}
								error={errors?.first_name}
							/>
						</Col>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_lastNameInput"
								title="Last Name"
								placeholder="Enter Last Name"
								value={userProfileForm?.last_name}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										last_name: val,
									});
									if (errors && errors.last_name) {
										delete errors.last_name;
										setErrors(errors);
									}
								}}
								error={errors?.last_name}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={24} lg={24} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_companyInput"
								title="Company"
								placeholder="Enter Company Name"
								value={userProfileForm?.company_name}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										company_name: val,
									});
									if (errors && errors.company_name) {
										delete errors.company_name;
										setErrors(errors);
									}
								}}
								error={errors?.email}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={24} lg={24} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_phoneInput"
								title="Phone"
								placeholder="Enter Phone Number"
								value={userProfileForm?.phone}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										phone: val,
									});
									if (errors && errors.phone) {
										delete errors.phone;
										setErrors(errors);
									}
								}}
								onCountryChange={() => {}}
								type="phone"
								country="US"
								error={errors?.phone}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={24} lg={24} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_emailInput"
								title="Email"
								placeholder="Enter Email"
								value={userProfileForm?.email}
								disabled={true}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										email: val,
									});
									if (errors && errors.email) {
										delete errors.email;
										setErrors(errors);
									}
								}}
								error={errors?.email}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={24} lg={24} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_addressLine1Input"
								title="Address Line 1"
								placeholder="Enter Address Line 1"
								value={userProfileForm?.address?.address}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										address: {
											...userProfileForm?.address,
											address: val,
										},
									});
									if (errors && errors?.address?.address) {
										delete errors.address?.address;
										setErrors(errors);
									}
								}}
								error={errors?.address?.address}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={24} lg={24} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_addressLine2Input"
								title="Address Line 2"
								placeholder="Enter Address Line 2"
								value={userProfileForm?.address?.address2}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										address: {
											...userProfileForm.address,
											address2: val,
										},
									});
									if (errors && errors.address?.address2) {
										delete errors.address?.address2;
										setErrors(errors);
									}
								}}
								error={errors?.address?.address2}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							<RASelectCountry
								data-testid="EditProfile_countrySelect"
								title="Country"
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										address: {
											...userProfileForm.address,
											country: val,
											state: "",
										},
									});
									if (errors && errors?.address?.country) {
										delete errors.address?.country;
										setErrors(errors);
									}
								}}
								value={userProfileForm?.address?.country}
								error={errors?.address?.country}
							/>
						</Col>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_cityInput"
								title="City"
								placeholder="Enter City"
								value={userProfileForm?.address?.city}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										address: {
											...userProfileForm.address,
											city: val,
										},
									});
									if (errors && errors.address?.city) {
										delete errors.address?.city;
										setErrors(errors);
									}
								}}
								error={errors?.address?.city}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 12]} style={{ marginTop: "12px" }}>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							{userProfileForm.address?.country?.short_code === US_SHORT_CODE ? (
								<RASelectUSStates
									data-testid="EditProfile_stateInput"
									title="State"
									value={userProfileForm?.address?.state}
									onChange={(val) => {
										setUserProfileForm({
											...userProfileForm,
											address: {
												...userProfileForm.address,
												state: val,
											},
										});
										if (errors && errors.address?.state) {
											delete errors.address?.state;
											setErrors(errors);
										}
									}}
									error={errors?.address?.state}
								/>
							) : (
								<RATextInput
									data-testid="EditProfile_stateInput"
									title="State"
									placeholder="Enter State"
									value={userProfileForm?.address?.state}
									onChange={(val) => {
										setUserProfileForm({
											...userProfileForm,
											address: {
												...userProfileForm.address,
												state: val,
											},
										});
										if (errors && errors.address?.state) {
											delete errors.address?.state;
											setErrors(errors);
										}
									}}
									error={errors?.address?.state}
								/>
							)}
						</Col>
						<Col xl={12} lg={12} md={24} sm={24} xs={24}>
							<RATextInput
								data-testid="EditProfile_zipCodeInput"
								title="Zip Code"
								placeholder="Enter Zip Code"
								value={userProfileForm?.address?.zip_code}
								onChange={(val) => {
									setUserProfileForm({
										...userProfileForm,
										address: {
											...userProfileForm.address,
											zip_code: val,
										},
									});
									if (errors && errors.address?.zip_code) {
										delete errors.address?.zip_code;
										setErrors(errors);
									}
								}}
								error={errors?.address?.zip_code}
							/>
						</Col>
					</Row>
					<WrapperBottom justify="center">
						<RAButtonPrimary
							data-testid="EditProfile_submitBtn"
							title="SUBMIT"
							width="180px"
							loading={loading}
							onClick={onSubmitEditUserProfile}
						/>
					</WrapperBottom>
				</FormInputWrapper>
			</FormContainer>
		</Modal>
	);
};

const FormContainer = styled.div`
	padding-top: 30px;
	padding-bottom: 30px;
	max-width: 100%;
	min-width: 343px;
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
const FormInputWrapper = styled.div`
	align-self: center;
`;
const TitleText = styled.div`
	font-family: "Gilda Display";
	font-style: normal;
	font-weight: 400;
	font-size: 24px;
	line-height: 28px;
	text-transform: capitalize;
	color: #121216;
	margin-bottom: 20px;
	padding-left: 20px;
	border-left: 2px solid #ffb11a;
`;
const WrapperBottom = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-top: 30px;
	gap: 12px;
`;
